import { Capability as CapabilityEnum, requires } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { getExtension, hasExtension } from '@bufbuild/protobuf';
/*
 * entitlements — the TS mirror of the platform's PLAN layer, and the composed
 * decision every product UI renders from.
 *
 * Source of truth: packages/trust/authz.Evaluate and packages/entitlements.
 * Keep IN SYNC. Like authz.ts, this exists so a UI can grey a control the server
 * would refuse — it is NOT the boundary. The server runs the identical
 * composition on every call and refuses regardless of what a UI drew.
 *
 * TWO PLANES, ONE ANSWER
 *
 * A control is available when the USER may perform the action (the trust layer,
 * authz.ts) AND the ORG bought it (the money layer, here). They are independent,
 * they fail for different reasons, and a UI must say which — "ask your admin" and
 * "upgrade your plan" send a person to completely different places, and guessing
 * wrong wastes their time and a support ticket.
 *
 * That is why evaluate() returns EVERY denial rather than the first: an action can
 * be refused on both planes at once, and a member on a free tier who is told only
 * "upgrade" will upgrade and still be blocked.
 *
 * WHERE THE PLAN COMES FROM
 *
 * SessionEntitlements.Subscribe on the api-gateway, which serves it from the same
 * resolver its authorization gate consults. Never from the session token: a plan
 * changes when money moves, not when a token refreshes, and a copy in the JWT
 * would be stale by exactly the window a customer is most likely to be looking.
 */

import { type Identity } from './identity';
import { type Permission, canFrom, isSuperAdmin, permissionOf } from './authz';

/** Where a capability sits in its lifecycle. Mirrors vitesse.v1.Lifecycle. */
export type Lifecycle = 'unspecified' | 'active' | 'grace' | 'expired';

/** One capability, resolved for the caller's scope. Mirrors vitesse.v1.FeatureConfig. */
export interface Capability {
  feature: string;
  active: boolean;
  lifecycle: Lifecycle;
  /** Resolved numeric ceilings. An ABSENT key means unlimited, never zero. */
  limits: Readonly<Record<string, number>>;
  /** When a lapsed capability stops working, for a countdown. 0 = not lapsing. */
  graceUntilMs: number;
  /** A GRADUATED level, e.g. telemetry "flows". Absent when not that kind. */
  choice?: string;
  /** A SET, e.g. which event kinds reach this org. Absent when not that kind. */
  members?: readonly string[];
}

/**
 * The caller's plan: every capability in the catalog, including the ones they do
 * NOT have — a UI needs the absent entries to render an upgrade path.
 *
 * `undefined` means NOT YET KNOWN (the stream has not delivered, or entitlements
 * are unreachable). That is deliberately distinct from "has nothing": see
 * hasCapability.
 */
export interface Plan {
  orgId: string;
  /** The relay this plan applies to. Empty = the org-wide subscription. */
  gatewayId: string;
  capabilities: readonly Capability[];
}

/** The capability keys the platform gates on. Mirrors packages/entitlements' catalog. */
/**
 * CAPABILITY_CATALOG — every capability the platform gates on, DERIVED from the contract enum.
 *
 * This is the correctness-critical list: a capability present in the proto and
 * absent here is invisible to the UI, so a paying customer sees nothing for
 * something they bought. Deriving makes that unrepresentable and removes the
 * mirror test that used to stand between the two.
 *
 * The generated enum already strips the CAPABILITY_ prefix (CLOUD_RECORDING = 1),
 * so the wire key is the lowercased name.
 */
export const CAPABILITY_CATALOG: readonly string[] = Object.freeze(
  Object.values(CapabilityEnum)
    .filter((v): v is CapabilityEnum => typeof v === 'number' && v !== 0)
    .sort((a, b) => a - b)
    .map((v) => CapabilityEnum[v].toLowerCase()),
);

/**
 * Readable aliases for the keys above. ERGONOMICS ONLY — deliberately NOT derived.
 *
 * Deriving these too would mean generating accessor names from enum names, which
 * turns RBAC into `Rbac` and IT_INTEGRATIONS into `ItIntegrations`: worse to read,
 * and churn at every call site whenever a capability is renamed. Nothing breaks if
 * an alias is missing — a call site writes the string — so the tradeoff is
 * legibility against a risk that does not exist.
 *
 * A test asserts every alias is a real CAPABILITY_CATALOG key, which catches the one mistake
 * this shape allows: a typo.
 */
export const Feature = {
  CloudRecording: 'cloud_recording',
  CloudHistorian: 'cloud_historian',
  Marketplace: 'marketplace',
  ProgramBackup: 'program_backup',
  NetworkSegmentation: 'network_segmentation',
  Telemetry: 'telemetry',
  Events: 'events',
  RBAC: 'rbac',
  /** Graduated (none|basic|standard|full): how much of the fleet UI a person
   *  sees — overview+software, +hardware+network+alerts, +map. */
  FleetManager: 'fleet_manager',
  /** Graduated (none|paywall|detection|isolation): the intrusion-detection surface. */
  IDS: 'ids',
  ITIntegrations: 'it_integrations',
  AiConsole: 'ai_console',
  /** The floor: does this org have a plan at all. See FeaturePlatform in policy.go. */
  Platform: 'platform',
  /** Always granted, gated anyway — see FeatureOnboarding in policy.go. */
  Onboarding: 'onboarding',
} as const;

/**
 * The numeric ceilings a capability can carry. Mirrors packages/entitlements'
 * limit keys.
 *
 * Named rather than typed inline at each call site: a UI reading a quota must use
 * the SAME key the executor enforces on, and a typo does not fail — it reads as an
 * absent limit, which means unlimited. A customer who is in fact capped would be
 * shown "unlimited", which is the worst available direction for that mistake.
 */
export const Limit = {
  RetentionDays: 'retention_days',
  MaxBytes: 'max_bytes',
  RetentionHours: 'retention_hours',
  MaxTable: 'max_table',
  MaxTopic: 'max_topic',
  MaxEndpoint: 'max_endpoint',
  MaxUsers: 'max_users',
  MaxDashboards: 'max_dashboards',
  MaxBandwidthBytes: 'max_bandwidth_bytes',
} as const;
export type LimitKey = (typeof Limit)[keyof typeof Limit];

/**
 * Does the org's plan currently include `feature`?
 *
 * An UNKNOWN plan answers TRUE, which is the opposite of the server's posture and
 * is correct here. This is a rendering hint, not a gate: if the plan has not
 * arrived, showing the control and letting the server refuse costs one clear
 * error, while hiding it tells a paying customer they own nothing every time
 * their connection blips. The server is fail-closed precisely so this can afford
 * to be fail-open.
 */
export function hasCapability(plan: Plan | undefined, feature: string): boolean {
  if (!plan) return true; // not yet known — see above
  const c = plan.capabilities.find((x) => x.feature === feature);
  return c?.active ?? false;
}

/**
 * Whether a graduated capability is AT or ABOVE a level.
 *
 * Two preconditions, both established by the only caller:
 *
 *   - the plan EXISTS. "Not yet known" is fail-OPEN and belongs with the other
 *     fail-open checks in `evaluate`, not hidden here, where a reader deciding
 *     whether a refusal is real would have to know this returns true for no plan.
 *   - the capability is ACTIVE. `evaluate` reaches the level check only after
 *     `hasCapability` passed, which already means active.
 *
 * So there is no guard for either. An unmatched feature still resolves safely:
 * `meetsLevel` ranks '' below every real level, so it returns false with no branch.
 */
function levelOk(plan: Plan, feature: string, want: string): boolean {
  const c = plan.capabilities.find((x) => x.feature === feature);
  return meetsLevel(feature, c?.choice ?? '', want);
}

/**
 * A resolved numeric ceiling, or undefined for unlimited.
 *
 * Undefined is UNLIMITED, not zero — an unbounded plan omits the key entirely. A
 * caller treating a missing limit as 0 would show "0 of 0 used" to the customer
 * paying for uncapped.
 */
export function limit(plan: Plan | undefined, feature: string, key: string): number | undefined {
  const c = plan?.capabilities.find((x) => x.feature === feature);
  const v = c?.limits?.[key];
  return v === undefined ? undefined : v;
}

/** One gate's refusal. Mirrors authz.Denial. */
export interface Denial {
  /** true = the ORG's plan refused (send them to billing); false = the USER's
   *  permission refused (send them to their org admin). */
  entitlement: boolean;
  /** The refused key — machine-usable, never the rendered message. */
  missing: string;
  explanation: string;
  remedy: string;
}

/** What one action needs. Mirrors authz.Requirement. Omit a field to skip its layer. */
export interface Requirement {
  permission?: Permission;
  capability?: string;
  /** For a graduated capability, the minimum level (telemetry "flows"). Checked
   *  HERE rather than by each caller: an org that HAS telemetry but only at
   *  "basic" is refused, and a caller that forgot this check would enable a
   *  control the server then refuses. */
  level?: string;
  /**
   * The metered resource one call of this kind spends a unit of, e.g.
   * "max_dashboards". Mirrors authz.Requirement.Consumes.
   *
   * Distinct from `limit()`, which reads a CLAMP. A clamp narrows a result; a
   * count REFUSES the call. Keeping them apart is what lets a control be
   * disabled with "3 of 3 in use" BEFORE the click rather than after a failed
   * round-trip — which is the whole reason the quantitative layer is mirrored
   * here at all rather than left server-side.
   */
  consumes?: string;
  /**
   * PLATFORM-OPERATOR only — no customer, at any plan, in any role.
   *
   * A floor rather than a ceiling, so it is not a permission: a permission is
   * grantable and this must not be. Mirrors authgate.Rule.SuperAdmin, and a UI
   * that ignored it would render a control the server refuses for everyone
   * outside Vitesse — which is the shape of lie this layer exists to prevent.
   */
  superAdmin?: boolean;
}

/**
 * How many of each metered resource the org currently holds, by limit key.
 *
 * `undefined`, or an absent key, means NOT KNOWN — never zero. The two are
 * opposite: rendering "0 of 3 in use" for an unknown count invites a user to
 * create something the server will refuse, which is the exact failure this
 * layer exists to prevent. An unknown count declines to disable the control and
 * lets the server answer, matching authz.Meter's `known == false`.
 */
export type Usage = Readonly<Record<string, number>>;

/**
 * THE decision — the mirror of authz.Evaluate, and the one function a UI should
 * call to decide whether to enable a control.
 *
 * Returns every gate that refused; empty means allowed. Resolution order matches
 * the server exactly: super-admin bypasses both layers, then the permission layer,
 * then the plan layer, with the permission denial FIRST so a UI that renders only
 * the primary reason shows the one the user can act on themselves.
 *
 * The strings are deliberately close to the server's. A UI can render these
 * directly and stay consistent with the message the server would have returned had
 * the call gone through — the same text either way, rather than a cheerful local
 * guess followed by a differently-worded refusal.
 */
export function evaluate(
  id: Identity,
  req: Requirement,
  plan: Plan | undefined,
  usage?: Usage,
): Denial[] {
  /*
   * PLATFORM-ONLY is checked BEFORE the super-admin bypass, because it is a floor
   * and the bypass is a ceiling — a requirement expressed after it could never
   * refuse the people it is about. Mirrors the Go gate exactly.
   *
   * The remedy is deliberately absent-shaped: there is nothing the person can do,
   * and a remedy that does not exist ("request access", "upgrade") is worse than
   * none. A UI reading this should HIDE the control rather than lock it.
   */
  if (req.superAdmin && !isSuperAdmin(id)) {
    return [
      {
        entitlement: false,
        missing: 'platform-operator',
        explanation: 'this is a Vitesse platform-operator action',
        remedy: '',
      },
    ];
  }
  if (isSuperAdmin(id)) return [];
  const denials: Denial[] = [];
  if (req.permission && !canFrom(id, req.permission)) {
    denials.push({
      entitlement: false,
      missing: req.permission,
      // Names the requirement, not the person: "requires X" is what an admin acts
      // on and what a support ticket can quote, where "you are not granted X" put
      // the reader at fault for a setting they cannot see. The permission KEY stays
      // in the sentence deliberately — it is the exact string an administrator
      // grants, so a prettier name would just have to be translated back.
      // Mirrors authz.go Evaluate.
      explanation: `requires the ${req.permission.replace(/^org:/, '')} permission`,
      remedy: 'ask your organization admin for access',
    });
  }
  if (req.capability && !hasCapability(plan, req.capability)) {
    denials.push({
      entitlement: true,
      missing: req.capability,
      explanation: `your organization's plan does not include ${req.capability}`,
      remedy: 'upgrade your plan to enable this',
    });
  } else if (req.capability && req.level && plan && !levelOk(plan, req.capability, req.level)) {
    // HAS the capability, just not enough of it. A distinct sentence, because
    // "your plan does not include telemetry" is false and reads as a bug to
    // someone looking at their own telemetry panel.
    denials.push({
      entitlement: true,
      missing: `${req.capability}:${req.level}`,
      explanation: `your organization's ${req.capability} plan is below the ${req.level} level`,
      remedy: 'upgrade your plan to enable this',
    });
  }
  // The quantitative layer — last, so a UI showing one reason shows the one the
  // user can act on themselves first.
  if (req.consumes && plan) {
    const used = usage?.[req.consumes];
    const cap = req.capability ? limit(plan, req.capability, req.consumes) : undefined;
    if (used !== undefined && cap !== undefined && used >= cap) {
      denials.push({
        entitlement: true,
        missing: req.consumes,
        // Same sentence the server builds, numbers included. "You have reached
        // your limit" is the message that generates the support ticket.
        explanation: `your organization's plan allows ${cap} and ${used} are in use`,
        remedy: 'remove one, or upgrade your plan for more',
      });
    }
  }
  return denials;
}

/** Convenience over evaluate() for the common "should this control be enabled?". */
export function allowed(
  id: Identity,
  req: Requirement,
  plan: Plan | undefined,
  usage?: Usage,
): boolean {
  return evaluate(id, req, plan, usage).length === 0;
}

/**
 * Why a control is disabled, as one sentence, or undefined when it is enabled.
 *
 * Every refusal is joined rather than only the first: an action can fail on both
 * planes, and a member on a free tier told only "upgrade" will pay and still be
 * blocked. This is the same rendering the server's denial suite produces, so the
 * tooltip before the click and the error after it read alike.
 */
export function whyDisabled(
  id: Identity,
  req: Requirement,
  plan: Plan | undefined,
  usage?: Usage,
): string | undefined {
  const denials = evaluate(id, req, plan, usage);
  if (denials.length === 0) return undefined;
  return denials.map((d) => `${d.explanation} — ${d.remedy}`).join('; and ');
}


/* ── Graduated capabilities ──────────────────────────────────────────────────
 *
 * A capability is not always a boolean. Some are LEVELS, and the ordering is the
 * API — expressed as ranks, never by comparing the strings.
 *
 * RBAC is why: its order is none < profiles < per_user, but alphabetically
 * "per_user" sorts BEFORE "profiles", so a text comparison would refuse the most
 * expensive tier something the middle tier gets. Telemetry happens to sort
 * correctly, which is precisely why the ranks must be explicit — right by luck for
 * one capability and wrong for another is not a rule.
 *
 * Mirrors packages/entitlements.
 */

export const TelemetryLevel = {
  Basic: 'basic', // what exists, and what it is doing right now
  Flows: 'flows', // + the surfaces that explain why: logs, vitals, faster polling
  Full: 'full', // everything
} as const;
/** Level ORDER per graduated capability. Data, not string comparison. */
/** Exported so a test can compare it against the server's own table. It was
 *  private with a comment claiming it mirrors policy.go EXACTLY, which is the
 *  shape that agrees until it does not — and nothing could check while it was
 *  unreachable. See base-ui's levelranks-parity test. */
export const levelRanks: Readonly<Record<string, Readonly<Record<string, number>>>> = {
  [Feature.Telemetry]: { basic: 1, flows: 2, full: 3 },
  [Feature.RBAC]: { none: 1, profiles: 2, per_user: 3 },
  // Mirrors packages/entitlements/policy.go levelRanks EXACTLY. Omitting a
  // graduated capability here is not a missing feature — meetsLevel fail-closes
  // on an unranked capability, so the first `level:` gate on it would refuse
  // every caller INCLUDING the top tier. fleet_manager (the fleet UI's tabs +
  // map) and ids (the intrusion surface) are both graduated and gated in the UI.
  [Feature.FleetManager]: { none: 1, basic: 2, standard: 3, full: 4 },
  [Feature.IDS]: { none: 1, paywall: 2, detection: 3, isolation: 4 },
};

/**
 * Does `have` reach `want` for a graduated capability?
 *
 * Unknown capability, unknown level, or an unranked value all rank 0 and refuse.
 * Note this is the ONE place in the client mirror that fails CLOSED: an unknown
 * plan still fails open via hasCapability, but a level that is present and
 * unrecognised is a bug rather than a missing plan, and inventing access from a
 * value nobody defined is the wrong direction for a bug.
 */
export function meetsLevel(feature: string, have: string, want: string): boolean {
  const ranks = levelRanks[feature];
  if (!ranks) return false;
  return (ranks[want] ?? 0) > 0 && (ranks[have] ?? 0) >= (ranks[want] ?? 0);
}


/* ── Reading a requirement from the RPC itself ───────────────────────────────
 *
 * The server declares what guards a procedure as a method option, and the
 * browser reads the SAME annotation from the same generated descriptor. A UI that
 * states its own capability and level is a second copy of the policy and will
 * eventually disagree with the first; this cannot, because there is only one.
 */

/** The minimal shape of a generated method handle — avoids importing protobuf-es
 *  types into every consumer, and keeps this usable with any generated service. */
export interface MethodLike {
  proto?: { options?: unknown };
}

/**
 * The requirement a generated RPC declares, or undefined when it declares none
 * (a deliberately public procedure, or one the gate does not guard).
 *
 * Returns a `Requirement` — the same shape `allows`/`why` take — so a caller never
 * converts between "what the RPC declares" and "what the session evaluates". They
 * were separate types differing only in how `permission` was typed, which bought a
 * cast at every call site and nothing else.
 *
 * The permission is asserted into the `Permission` union because the ANNOTATION is
 * the vocabulary's source of truth and the union is its compile-time mirror. If the
 * two ever disagree, `canFrom` still resolves an unfamiliar string safely — via the
 * explicit grant, then the membership set, then the role.
 */
export function requirementFor(method: MethodLike | undefined): Requirement | undefined {
  const opts = method?.proto?.options;
  if (!opts || !hasExtension(opts as never, requires)) return undefined;
  const r = getExtension(opts as never, requires);
  const capability = r.capability ? CapabilityEnum[r.capability]?.toLowerCase() : '';
  // Prefer the typed enum over the legacy string, exactly as the Go gate does.
  // They cannot disagree in practice — a procedure states one or the other — but
  // the enum wins when both are set, because it is the one a typo cannot survive.
  const permission = permissionOf(r.perm) || r.permission;
  return {
    permission: (permission || undefined) as Permission | undefined,
    capability: capability || undefined,
    level: r.level || undefined,
    superAdmin: r.superAdmin || undefined,
  };
}
